import { Add, GetApp, FilterList, Close, Search, ExpandMore, Refresh } from "@mui/icons-material";
import { Box, Button, Card, styled, useTheme, Grid, Tabs, Tab, Badge, alpha, useMediaQuery, TextField, InputAdornment, IconButton, Collapse, FormControl, InputLabel, Select, MenuItem, Chip, Tooltip, Skeleton } from "@mui/material";
import LocationCityIcon from '@mui/icons-material/LocationCity';
import Public from '@mui/icons-material/Public';
import LibraryBooks from '@mui/icons-material/LibraryBooks';
import CurrencyRupee from '@mui/icons-material/CurrencyRupee'
import FlexBetween from "components/flexbox/FlexBetween";
import FlexBox from "components/flexbox/FlexBox";
import SearchInput from "components/input-fields/SearchInput";
import { H6 } from "components/Typography";
import DataTable from "page-sections/data-table/dataTableV2/DataTable";
import LeadCard from "page-sections/dashboards/CRM/LeadCard";
import LongCard from "page-sections/dashboards/CRM/LongCard";
import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import AddDistrictModal from "page-sections/data-table/dataTableV2/AddDistrictModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { usePageTitle } from "contexts/PageTitleContext";
import { Typography } from "@mui/material";
import ProductList, { consumeSavedAppOrderStatusTab, consumeSavedAppOrderListState } from "./productList";
import {
  setAppOrderListSnapshot,
  takeAppOrderListSnapshotIfReturning,
  acknowledgeAppOrderListRestore,
  isAppOrderListReturnPending,
} from "./appOrderListCache";
import AdvancedExportModal from "./AdvancedExportModal";
import AppPaginationBar from "components/AppPaginationBar";
import { getPermissionValue, isOrderStatusTabDisabled, mergeOrderStatusLists } from "config/ConfigAccessButton";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";


// Styled Components for Filters
const FilterContainer = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  overflow: 'visible',
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
  [theme.breakpoints.down('sm')]: {
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
}));


const FilterHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  background: alpha(theme.palette.primary.main, 0.02),
  borderRadius: `${theme.spacing(2)} ${theme.spacing(2)} 0 0`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 2),
  },
}));

const FilterContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  position: 'relative',
  zIndex: 1000,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  '& .MuiTextField-root': {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      },
      '&.Mui-focused': {
        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
      },
    },
  },
}));

const DatePickerField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.background.paper,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    '&.Mui-focused': {
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
  },
  '& .MuiInputBase-input': {
    cursor: 'pointer',
    colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light',
  },
  '& input[type="date"]': {
    colorScheme: theme.palette.mode === 'dark' ? 'dark' : 'light',
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'invert(0)',
    cursor: 'pointer',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  boxShadow: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.75, 2),
    fontSize: '0.875rem',
  },
}));

const StatusChip = styled(Card)(({ theme, statuscolor }) => ({
  fontWeight: 600,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5),
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: statuscolor ? alpha(statuscolor, 0.1) : alpha(theme.palette.grey[500], 0.1),
  color: statuscolor || theme.palette.grey[700],
  border: `1px solid ${statuscolor ? alpha(statuscolor, 0.2) : alpha(theme.palette.grey[500], 0.2)}`,
  fontSize: '0.75rem',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.25, 1),
    fontSize: '0.7rem',
  },
}));

// Export utility functions
const exportToCSV = (data, filename = 'products_export.csv') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  const headers = [
    'Order ID',
    'User Name',
    'User Email',
    'Mobile Number',
    'Status',
    'Payment Status',
    'Payment Method',
    'Total Amount',
    'Order Date',
    'Delivery Address',
    'Created At'
  ];

  const csvContent = [
    headers.join(','),
    ...data.map(product => [
      `"${product.order_id || ''}"`,
      `"${product.user?.username || product.user_name || ''}"`,
      `"${product.user?.email || product.user_email || ''}"`,
      `"${product.user?.mobile_number || product.mobile_number || ''}"`,
      `"${product.status?.name || product.order_status_display || ''}"`,
      `"${product.payment_status?.name || product.payment_status_display || ''}"`,
      `"${product.payment_method?.method || product.payment_method_name || ''}"`,
      `"${product.total_amount || ''}"`,
      `"${product.order_date_formatted || ''}"`,
      `"${product.delivery_address || (product.address_details ? `${product.address_details.full_address}, ${product.address_details.village}` : '')}"`,
      `"${product.created_at ? new Date(product.created_at).toLocaleDateString() : ''}"`
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToExcel = async (data, filename = 'products_export.xlsx') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  try {
    const XLSX = await import('xlsx');
    
    const excelData = data.map(product => ({
      'Order ID': product.order_id || '',
      'User Name': product.user?.username || product.user_name || '',
      'User Email': product.user?.email || product.user_email || '',
      'Mobile Number': product.user?.mobile_number || product.mobile_number || '',
      'Status': product.status?.name || product.order_status_display || '',
      'Payment Status': product.payment_status?.name || product.payment_status_display || '',
      'Payment Method': product.payment_method?.method || product.payment_method_name || '',
      'Total Amount': product.total_amount || 0,
      'Order Date': product.order_date_formatted || '',
      'Delivery Address': product.delivery_address || (product.address_details ? `${product.address_details.full_address}, ${product.address_details.village}` : ''),
      'Created At': product.created_at ? new Date(product.created_at).toLocaleDateString('en-IN') : ''
    }));

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const colWidths = Object.keys(excelData[0] || {}).map((key, index) => {
      let maxWidth = key.length;
      excelData.forEach(row => {
        const cellValue = String(row[key] || '');
        maxWidth = Math.max(maxWidth, cellValue.length);
      });
      const width = Math.min(Math.max(maxWidth + 2, 12), 50);
      return { wch: width };
    });
    
    worksheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Products Export');

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const finalFilename = filename.replace('.xlsx', `_${timestamp}.xlsx`);
    
    XLSX.writeFile(workbook, finalFilename);
    
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    alert('Error exporting to Excel. Please try again or use CSV export instead.');
  }
};

const ButtonWrapper = styled(FlexBox)(({
  theme
}) => ({
  [theme.breakpoints.down(500)]: {
    marginTop: 10,
    width: "100%",
    flexDirection: "column-reverse",
    "& > .MuiBox-root": {
      width: "100%",
      margin: "10px 0",
      alignItems: "center",
      flexDirection: "column"
    },
    "& .MuiButton-root": {
      minWidth: "100%"
    }
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  minHeight: 42,
  width: '100%',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTabs-flexContainer': {
    gap: 6,
    padding: 6,
  },
  '& .MuiTabs-scroller': {
    [theme.breakpoints.up('md')]: {
      '& .MuiTabs-flexContainer': {
        justifyContent: 'space-between',
      },
    },
  },
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 2px 8px rgba(0,0,0,0.3)' 
    : '0 2px 8px rgba(0,0,0,0.1)',
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8125rem',
  minHeight: 34,
  borderRadius: theme.shape.borderRadius,
  margin: 0,
  padding: '6px 12px',
  color: theme.palette.text.secondary,
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  
  [theme.breakpoints.up('md')]: {
    flex: 1,
    minWidth: 0,
  },
  
  [theme.breakpoints.down('md')]: {
    minWidth: 112,
    padding: '6px 10px',
    fontSize: '0.8rem',
  },
  
  [theme.breakpoints.down('sm')]: {
    minWidth: 96,
    padding: '6px 8px',
    fontSize: '0.75rem',
  },

  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },
  '&.Mui-disabled': {
    opacity: 0.45,
    cursor: 'not-allowed',
    color: theme.palette.text.disabled,
    pointerEvents: 'auto',
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const OrderStatusCountBadge = styled('span', {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 32,
  height: 26,
  padding: '4px 10px',
  borderRadius: 999,
  fontSize: '0.875rem',
  fontWeight: 800,
  lineHeight: 1,
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums',
  letterSpacing: '0.02em',
  ...(active
    ? {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.primary.main,
        border: `1.5px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
      }
    : {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        border: `1.5px solid ${alpha(theme.palette.primary.dark, 0.35)}`,
        boxShadow: '0 2px 6px rgba(25, 118, 210, 0.28)',
      }),
}));

const getStatusColor = (theme, status) => {
  const colors = {
    all: theme.palette.text.primary,
    pending: theme.palette.warning.main,
    confirmed: theme.palette.info.main,
    processing: theme.palette.primary.main,
    shipped: theme.palette.secondary.main,
    delivered: theme.palette.success.main,
    cancelled: theme.palette.error.main,
    returned: theme.palette.error.dark,
  };
  return colors[status] || theme.palette.grey[500];
};

const TabContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  position: 'sticky',
  top: 0,
  zIndex: 10,
  backgroundColor: theme.palette.background.default,
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
  },
}));

const SearchControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
  },
  
  [theme.breakpoints.up('lg')]: {
    gap: theme.spacing(3),
  },
}));

const ActionButtonsContainer = styled(FlexBox)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.5),
    width: '100%',
    
    '& > *': {
      width: '100%',
    },
  },
  
  [theme.breakpoints.up('lg')]: {
    gap: theme.spacing(3),
  },
}));

const Products = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const { setPageTitle } = usePageTitle();

  const isCompanyUser = apiService.isCompanyUser();
  const companyId = apiService.getCompanyId();
  const [districts, setDistricts] = useState([]);
  const [summaryTotals, setSummaryTotals] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBlinking, setIsBlinking] = useState(false);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(1)));
  const [endDate, setEndDate] = useState(new Date());

  const [products, setProducts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];
  const [showFilters, setShowFilters] = useState(false);
  const [isResetClicked, setIsResetClicked] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(isCompanyUser ? companyId : 'all');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPaymentType, setSelectedPaymentType] = useState('all');
  const PAYMENT_TYPE_OPTIONS = [
    { value: 'Advance Payment', label: 'Advance Payment' },
    { value: 'Full Payment', label: 'Full Payment' },
    { value: 'Credit', label: 'Credit' },
  ];
  
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  
  const [advancedExportModal, setAdvancedExportModal] = useState(false);
  
  const isFetchingStatuses = useRef(false);
  
  // Get current tab name based on status filter
  const getCurrentTabName = () => {
    const tabMapping = {
      'Placed': 'Placed Button',
      'Confirmed': 'Confirmed Button',
      'Processing': 'Processing Button',
      'Dispatched': 'Dispatch Button',
      'Dispatch': 'Dispatch Button',
      'Delivered': 'Delivered Button',
      'Cancelled': 'Cancelled Button',
      'Returned': 'Returned Button'
    };
    return tabMapping[statusFilter] || 'Placed Button';
  };
  
  // Force re-render when Permission tab saves (logged_data synced)
  const [permissionTick, setPermissionTick] = useState(0);
  useEffect(() => {
    const onPermissionsUpdated = () => setPermissionTick((v) => v + 1);
    window.addEventListener("permissions-updated", onPermissionsUpdated);
    return () => window.removeEventListener("permissions-updated", onPermissionsUpdated);
  }, []);

  // Live permission check from localStorage (same source as Send Email / ConfigAccessButton)
  const shouldEnableButton = (buttonKey) => {
    void permissionTick;
    return getPermissionValue("appOrders", null, getCurrentTabName(), buttonKey) === true;
  };

  const getStatusIcon = (category) => {
    const key = (category || '').toLowerCase();
    switch (key) {
      case 'pending': return '⏳';
      case 'confirmed': return '✅';
      case 'processing': return '⚙️';
      case 'shipped': return '🚚';
      case 'delivered': return '📦';
      case 'cancelled': return '❌';
      case 'returned': return '↩️';
      case 'all': return '📊';
      default: return '🏷️';
    }
  };

  useEffect(() => {
    setPageTitle('App Order');
  }, [setPageTitle]);

  const fetchCompanies = async () => {
    try {
      if (isCompanyUser && companyId) {
        const companyDetails = apiService.getCompanyDetails();
        if (companyDetails) {
          setCompanies([companyDetails]);
        }
        return;
      }
      
      const res = await apiService.get(API_ENDPOINTS.GET_company);
      if (res?.success) {
        const companyList = res.data?.Company || [];
        setCompanies(companyList);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const categoriesFetchIdRef = useRef(0);

  const fetchCategories = async (companyFilter = 'all') => {
    const fetchId = ++categoriesFetchIdRef.current;
    try {
      // When scoping to one company, clear first so other companies' categories are not shown
      if (companyFilter !== 'all') {
        setCategories([]);
      }
      let response;
      if (companyFilter === 'all') {
        response = await apiService.get(API_ENDPOINTS.GET_product_categories);
      } else {
        const userId = localStorage.getItem('user_id');
        response = await apiService.get(
          `${API_ENDPOINTS.GET_product_category}?company=${companyFilter}&user_id=${userId}`
        );
      }
      // Ignore stale responses so an earlier "all" fetch cannot overwrite company-scoped results
      if (fetchId !== categoriesFetchIdRef.current) return;
      if (response?.success) {
        const list =
          response.data?.ProductCategory ||
          response.data?.product_category ||
          response.data?.categories ||
          (Array.isArray(response.data) ? response.data : []) ||
          [];
        setCategories(Array.isArray(list) ? list : []);
      } else {
        setCategories([]);
      }
    } catch (error) {
      if (fetchId !== categoriesFetchIdRef.current) return;
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  useEffect(() => {
    const initializeFilters = async () => {
      await fetchCompanies();
      // On Back from order details, restore effect loads company-scoped categories — skip "all"
      if (isAppOrderListReturnPending()) return;
      if (isCompanyUser && companyId) {
        await fetchCategories(companyId);
      } else {
        await fetchCategories('all');
      }
    };
    initializeFilters();
  }, []);

  // Only reload category options when company changes. Do NOT reset selectedCategory here —
  // that would wipe the category when restoring filters on Back. handleCompanyChange resets it.
  // Skip the initial 'all' fetch while Back-restore is pending (restore loads company categories).
  useEffect(() => {
    if (!isCompanyUser && selectedCompany) {
      if (
        selectedCompany === 'all' &&
        isAppOrderListReturnPending()
      ) {
        return;
      }
      fetchCategories(selectedCompany);
    }
  }, [selectedCompany, isCompanyUser]);

  const formatDateForAPI = (date) => {
    if (!date || date === '') {
      return null;
    }
    if (typeof date === 'string') {
      const trimmed = date.trim();
      if (!trimmed) return null;
      return trimmed.includes('T') ? trimmed.split('T')[0] : trimmed;
    }
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return null;
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  /** Local YYYY-MM-DD for date inputs — never use toISOString (UTC day-shift in IST). */
  const toDateInputValue = (date) => formatDateForAPI(date) || '';

  const formatSummaryAmount = (amount) => {
    const num = Number(amount);
    if (Number.isNaN(num)) return "—";
    return `₹${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDateChipLabel = (dateStr) => {
    if (!dateStr) return '';
    const parsed = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return dateStr;
    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const parseStoredFilterDate = (value) => {
    if (value == null || value === '') return null;
    if (typeof value !== 'string') return null;
    const dateOnly = value.includes('T') ? value.split('T')[0] : value.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null;
    return dateOnly; // keep as YYYY-MM-DD string to avoid Date/UTC display bugs
  };

  const fetchStatuses = async (
    preserveCurrentFilter = false,
    skipProductFetch = false,
    companyOverride = null,
    dateOverride = null,
    categoryOverride = null
  ) => {
    if (isFetchingStatuses.current) {
      return;
    }
    
    isFetchingStatuses.current = true;
    if (!skipProductFetch) {
      setIsLoading(true);
    }
    try {
      let effectiveStartDate =
        dateOverride?.startDate !== undefined ? dateOverride.startDate : startDate;
      let effectiveEndDate =
        dateOverride?.endDate !== undefined ? dateOverride.endDate : endDate;
      let effectiveCompany = companyOverride ?? selectedCompany;
      let effectiveCategory =
        categoryOverride !== null ? categoryOverride : selectedCategory;
      let restoredPage = currentPage;
      let restoredPageSize = pageSize;
      let restoredSearch = searchTerm;
      let savedTab = null;

      // Restore saved filters BEFORE building API URLs (keeps dates/company exact).
      if (!preserveCurrentFilter && !skipProductFetch) {
        savedTab = consumeSavedAppOrderStatusTab();
        const savedListState = consumeSavedAppOrderListState();
        if (savedListState) {
          const restoredStart = parseStoredFilterDate(savedListState.startDate);
          const restoredEnd = parseStoredFilterDate(savedListState.endDate);

          if (restoredStart) {
            effectiveStartDate = restoredStart;
            setStartDate(restoredStart);
          }
          if (restoredEnd) {
            effectiveEndDate = restoredEnd;
            setEndDate(restoredEnd);
          }
          // Only clear dates when user had explicitly Reset before opening details
          if (savedListState.datesCleared === true && !restoredStart && !restoredEnd) {
            effectiveStartDate = '';
            effectiveEndDate = '';
            setStartDate('');
            setEndDate('');
            setIsResetClicked(true);
          }

          if (
            !isCompanyUser &&
            savedListState.selectedCompany != null &&
            savedListState.selectedCompany !== ''
          ) {
            effectiveCompany = savedListState.selectedCompany;
            setSelectedCompany(savedListState.selectedCompany);
          }
          if (
            savedListState.selectedCategory != null &&
            savedListState.selectedCategory !== ''
          ) {
            effectiveCategory = savedListState.selectedCategory;
            setSelectedCategory(savedListState.selectedCategory);
          }
          if (typeof savedListState.showFilters === 'boolean') {
            setShowFilters(savedListState.showFilters);
          }

          restoredPage =
            Number(savedListState.page) > 0 ? Number(savedListState.page) : 1;
          restoredPageSize =
            Number(savedListState.pageSize) > 0
              ? Number(savedListState.pageSize)
              : pageSize;
          restoredSearch =
            typeof savedListState.searchTerm === 'string'
              ? savedListState.searchTerm
              : '';

          setCurrentPage(restoredPage);
          setPageSize(restoredPageSize);
          if (restoredSearch) {
            setSearchTerm(restoredSearch);
            setSearchValue(restoredSearch);
            setHasSearched(true);
          } else {
            setSearchTerm('');
            setSearchValue('');
            setHasSearched(false);
          }
        }
      }

      const userId = localStorage.getItem("user_id");
      const fromDate = formatDateForAPI(effectiveStartDate);
      const toDate = formatDateForAPI(effectiveEndDate);

      let countUrl = `${API_ENDPOINTS.GET_order_status_choices_with_count}?user_id=${userId}&type=normal`;
      
      if (fromDate) {
        countUrl += `&from_date=${fromDate}`;
      }
      if (toDate) {
        countUrl += `&to_date=${toDate}`;
      }
      
      let companyToUse;
      if (isCompanyUser && companyId) {
        companyToUse = companyId;
      } else {
        companyToUse = effectiveCompany;
      }
      if (companyToUse && companyToUse !== 'all') {
        countUrl += `&company=${companyToUse}`;
      }
      if (effectiveCategory && effectiveCategory !== 'all') {
        countUrl += `&category=${effectiveCategory}&product_category=${effectiveCategory}`;
      }

      // Full list (so denied statuses stay visible) + counts (may omit denied statuses)
      const [choicesResponse, countResponse] = await Promise.all([
        apiService.get(`${API_ENDPOINTS.GET_order_status_choices}?type=normal`),
        apiService.get(countUrl),
      ]);

      const allStatuses = Array.isArray(choicesResponse?.data)
        ? choicesResponse.data
        : Array.isArray(choicesResponse?.data?.statuses)
          ? choicesResponse.data.statuses
          : [];
      const countedStatuses = Array.isArray(countResponse?.data?.statuses)
        ? countResponse.data.statuses
        : Array.isArray(countResponse?.data)
          ? countResponse.data
          : [];

      const sorted =
        allStatuses.length > 0
          ? mergeOrderStatusLists(allStatuses, countedStatuses)
          : [...countedStatuses].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

      if (choicesResponse?.success || countResponse?.success || sorted.length > 0) {
        setStatuses(sorted);

        if (!skipProductFetch) {
          const summary = countResponse?.data?.summary;
          if (summary && (summary.total_orders != null || summary.total_amount != null)) {
            setSummaryTotals({
              total_orders: Number(summary.total_orders) || 0,
              total_amount: summary.total_amount ?? 0,
            });
          } else {
            setSummaryTotals(null);
          }

          if (preserveCurrentFilter && statusFilter && !isOrderStatusTabDisabled("appOrders", { name: statusFilter })) {
            fetchProducts(statusFilter, companyOverride, currentPage, pageSize, null, {
              startDate: effectiveStartDate,
              endDate: effectiveEndDate,
            }, null, effectiveCategory);
          } else {
            const savedTabExists =
              savedTab &&
              sorted.some((s) => s.name === savedTab) &&
              !isOrderStatusTabDisabled("appOrders", sorted.find((s) => s.name === savedTab) || savedTab);
            const firstAllowed =
              sorted.find((s) => !isOrderStatusTabDisabled("appOrders", s))?.name ||
              sorted[0]?.name ||
              "";
            const initial = savedTabExists ? savedTab : firstAllowed;
            setStatusFilter(initial);
            fetchProducts(
              initial,
              companyToUse,
              restoredPage,
              restoredPageSize,
              restoredSearch,
              {
                startDate: effectiveStartDate,
                endDate: effectiveEndDate,
              },
              null,
              effectiveCategory
            );
          }
        }
      } else if (!skipProductFetch) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching statuses:", error);
      setSummaryTotals(null);
      if (!skipProductFetch) {
        setIsLoading(false);
      }
    } finally {
      isFetchingStatuses.current = false;
    }
  };

  const orderMatchesSelectedCategory = (order, categoryId, categoryName) => {
    const idStr = categoryId != null ? String(categoryId) : '';
    const nameLc = (categoryName || '').trim().toLowerCase();

    const entityMatches = (entity) => {
      if (!entity || typeof entity !== 'object') return false;
      const ids = [
        entity.category_id,
        entity.product_category_id,
        entity.product_category,
        entity.category?.id,
        typeof entity.category === 'number' ? entity.category : null,
      ];
      if (idStr && ids.some((id) => id != null && id !== '' && String(id) === idStr)) {
        return true;
      }
      const names = [
        entity.category_name,
        entity.category?.name,
        typeof entity.category === 'string' ? entity.category : null,
      ];
      if (
        nameLc &&
        names.some((n) => n && String(n).trim().toLowerCase() === nameLc)
      ) {
        return true;
      }
      return false;
    };

    if (entityMatches(order)) return true;
    const items = Array.isArray(order?.items) ? order.items : [];
    return items.some(entityMatches);
  };

  const fetchProducts = async (
    status = statusFilter,
    companyOverride = null,
    page = currentPage,
    pageSizeParam = pageSize,
    searchQuery = null,
    dateOverride = null,
    paymentTypeOverride = null,
    categoryOverride = null
  ) => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem("user_id");
      const fromDate = formatDateForAPI(
        dateOverride?.startDate !== undefined ? dateOverride.startDate : startDate
      );
      const toDate = formatDateForAPI(
        dateOverride?.endDate !== undefined ? dateOverride.endDate : endDate
      );
      const statusParam = !status ? '' : `&status=${encodeURIComponent(status)}`;
      
      let url = `${API_ENDPOINTS.GET_products}?user_id=${userId}${statusParam}&type=normal`;
      
      if (fromDate) {
        url += `&from_date=${fromDate}`;
      }
      if (toDate) {
        url += `&to_date=${toDate}`;
      }
      
      const activeSearch = searchQuery !== null ? searchQuery : searchTerm;
      if (activeSearch && activeSearch.trim()) {
        url += `&search=${encodeURIComponent(activeSearch.trim())}`;
      }   
      
      let companyToUse;
      if (isCompanyUser && companyId) {
        companyToUse = companyId;
      } else {
        companyToUse = companyOverride ?? selectedCompany;
      }
      if (companyToUse && companyToUse !== 'all') {
        url += `&company=${companyToUse}`;
      }
      const categoryToUse =
        categoryOverride !== null ? categoryOverride : selectedCategory;
      const isCategoryFilterActive = Boolean(categoryToUse && categoryToUse !== 'all');
      const selectedCategoryMeta = isCategoryFilterActive
        ? categories.find((c) => String(c.id) === String(categoryToUse))
        : null;
      const categoryNameToUse = selectedCategoryMeta?.name || '';
      if (isCategoryFilterActive) {
        url += `&category=${categoryToUse}&product_category=${categoryToUse}`;
        if (categoryNameToUse) {
          url += `&category_name=${encodeURIComponent(categoryNameToUse)}`;
        }
      }
      const paymentTypeToUse =
        paymentTypeOverride !== null ? paymentTypeOverride : selectedPaymentType;
      if (paymentTypeToUse && paymentTypeToUse !== 'all') {
        url += `&payment_type=${encodeURIComponent(paymentTypeToUse)}`;
      }
      // When category is selected, load a larger page then filter/paginate client-side
      // (list-user-orders may ignore category query params).
      const requestPage = isCategoryFilterActive ? 1 : page;
      const requestPageSize = isCategoryFilterActive
        ? Math.max(Number(pageSizeParam) || 10, Number(pagination?.total_items) || 0, 500)
        : pageSizeParam;
      if (requestPage) url += `&page=${requestPage}`;
      if (requestPageSize) url += `&page_size=${requestPageSize}`;
      
      const response = await apiService.get(url);
      
      if (response) {
        let list = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.results)
            ? response.results
            : Array.isArray(response)
              ? response
              : [];

        let paginationData = response.pagination || response?.meta || response?.data?.pagination || response?.pagination || {};

        if (isCategoryFilterActive) {
          const orderHasCategoryMeta = (order) => {
            if (
              order?.category_name ||
              order?.category_id ||
              order?.product_category_id ||
              order?.product_category ||
              order?.category?.id ||
              order?.category?.name
            ) {
              return true;
            }
            const items = Array.isArray(order?.items) ? order.items : [];
            return items.some(
              (item) =>
                item?.category_name ||
                item?.category_id ||
                item?.product_category_id ||
                item?.product_category ||
                item?.category?.id ||
                item?.category?.name ||
                typeof item?.category === 'string'
            );
          };
          const canClientFilter = list.some(orderHasCategoryMeta);
          const filtered = canClientFilter
            ? list.filter((order) =>
                orderMatchesSelectedCategory(order, categoryToUse, categoryNameToUse)
              )
            : list;
          const size = Number(pageSizeParam) || 10;
          const current = Number(page) > 0 ? Number(page) : 1;
          const total = filtered.length;
          const totalPages = Math.max(1, Math.ceil(total / size) || 1);
          const safePage = Math.min(current, totalPages);
          const start = (safePage - 1) * size;
          list = filtered.slice(start, start + size);
          paginationData = {
            current_page: safePage,
            page_size: size,
            total_items: total,
            total_pages: totalPages,
            has_next: safePage * size < total,
            has_previous: safePage > 1,
          };
        }

        setProducts(list);

        setPagination(prev => ({
          current_page: paginationData.current_page ?? page,
          page_size: paginationData.page_size ?? pageSizeParam,
          total_items: paginationData.total_items ?? (Array.isArray(list) ? list.length : 0),
          total_pages: paginationData.total_pages ?? 1,
          has_next: paginationData.has_next ?? false,
          has_previous: paginationData.has_previous ?? false
        }));
        setCurrentPage(paginationData.current_page ?? page);
        setPageSize(paginationData.page_size ?? pageSizeParam);

        const summary = response?.summary;
        if (summary && (summary.total_orders != null || summary.total_amount != null) && !isCategoryFilterActive) {
          setSummaryTotals({
            total_orders: Number(summary.total_orders) || 0,
            total_amount: summary.total_amount ?? 0,
          });
        } else if (isCategoryFilterActive) {
          const filteredTotal = paginationData.total_items ?? list.length;
          setSummaryTotals({
            total_orders: filteredTotal,
            total_amount: summary?.total_amount ?? null,
          });
        } else {
          setSummaryTotals(null);
        }

        const companyForStatusUpdate = companyOverride ?? selectedCompany;
        fetchStatuses(true, true, companyForStatusUpdate, dateOverride, categoryToUse);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    const trimmedSearch = searchValue.trim();
    setSearchTerm(trimmedSearch);
    setCurrentPage(1);
    setHasSearched(true);
    fetchProducts(statusFilter, selectedCompany, 1, pageSize, trimmedSearch);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setSearchTerm("");
    setCurrentPage(1);
    setHasSearched(false);
    fetchProducts(statusFilter, selectedCompany, 1, pageSize, "");
  };

  useEffect(() => {
    const snapshot = takeAppOrderListSnapshotIfReturning();
    if (snapshot) {
      // Returning from order details — restore exact previous UI, no API refresh.
      consumeSavedAppOrderStatusTab();
      consumeSavedAppOrderListState();
      setProducts(Array.isArray(snapshot.products) ? snapshot.products : []);
      if (snapshot.pagination) setPagination(snapshot.pagination);
      setCurrentPage(Number(snapshot.currentPage) > 0 ? Number(snapshot.currentPage) : 1);
      setPageSize(Number(snapshot.pageSize) > 0 ? Number(snapshot.pageSize) : 10);
      setStatusFilter(snapshot.statusFilter || '');
      setStatuses(Array.isArray(snapshot.statuses) ? snapshot.statuses : []);
      setSearchTerm(snapshot.searchTerm || '');
      setSearchValue(snapshot.searchValue || '');
      setHasSearched(Boolean(snapshot.hasSearched));
      setStartDate(snapshot.startDate || '');
      setEndDate(snapshot.endDate || '');
      if (snapshot.selectedCompany != null) setSelectedCompany(snapshot.selectedCompany);
      if (snapshot.selectedCategory != null) setSelectedCategory(snapshot.selectedCategory);
      if (snapshot.selectedPaymentType != null) setSelectedPaymentType(snapshot.selectedPaymentType);
      // Always reload categories for the restored company so dropdown is not "all companies"
      const restoredCompany =
        snapshot.selectedCompany != null
          ? snapshot.selectedCompany
          : isCompanyUser
            ? companyId
            : 'all';
      if (Array.isArray(snapshot.categories) && snapshot.categories.length > 0) {
        setCategories(snapshot.categories);
      }
      fetchCategories(restoredCompany || 'all');
      setShowFilters(Boolean(snapshot.showFilters));
      setIsResetClicked(Boolean(snapshot.isResetClicked));
      if (snapshot.summaryTotals != null) setSummaryTotals(snapshot.summaryTotals);
      setIsLoading(false);
      acknowledgeAppOrderListRestore();
      return;
    }
    fetchStatuses(false, false, selectedCompany);
  }, []);

  // Keep an in-memory snapshot so Back can restore without refetching.
  useEffect(() => {
    setAppOrderListSnapshot({
      products,
      pagination,
      currentPage,
      pageSize,
      statusFilter,
      statuses,
      searchTerm,
      searchValue,
      hasSearched,
      startDate: toDateInputValue(startDate),
      endDate: toDateInputValue(endDate),
      selectedCompany,
      selectedCategory,
      categories,
      selectedPaymentType,
      showFilters,
      isResetClicked,
      summaryTotals,
    });
  }, [
    products,
    pagination,
    currentPage,
    pageSize,
    statusFilter,
    statuses,
    searchTerm,
    searchValue,
    hasSearched,
    startDate,
    endDate,
    selectedCompany,
    selectedCategory,
    categories,
    selectedPaymentType,
    showFilters,
    isResetClicked,
    summaryTotals,
  ]);

  useEffect(() => {
    if (searchValue.trim() === "" && searchTerm !== "") {
      handleClearSearch();
    }
  }, [searchValue]);

  useEffect(() => {
    if (showFilters && (!startDate || startDate === '') && (!endDate || endDate === '') && !isResetClicked) {
      setStartDate(toDateInputValue(new Date(new Date().setDate(1))));
      setEndDate(toDateInputValue(new Date()));
    }
    if (!showFilters) {
      setIsResetClicked(false);
    }
  }, [showFilters]);

  const handleStatusChange = (event, newValue) => {
    const statusItem = statuses.find((s) => s.name === newValue) || { name: newValue };
    if (isOrderStatusTabDisabled("appOrders", statusItem)) return;
    setStatusFilter(newValue);
    fetchProducts(newValue, selectedCompany, 1, pageSize);
  };

  const handleRefresh = async () => {
    await fetchStatuses(true, false, selectedCompany);
    fetchProducts(statusFilter, selectedCompany, currentPage, pageSize);
  };

  const handleRefreshWithBlink = async () => {
    setIsBlinking(true);
    try {
      await handleRefresh();
    } finally {
      setTimeout(() => setIsBlinking(false), 420);
    }
  };

  /** After order edit, map API `data.reason` to Edit Reason on the order row. */
  const handleOrderEditSuccess = async (updatedOrder) => {
    await handleRefreshWithBlink();
    // Re-apply edit API `data.reason` so Edit Reason isn't overwritten by list data
    if (updatedOrder?.order_id && updatedOrder?.reason != null) {
      setProducts((prev) =>
        (Array.isArray(prev) ? prev : []).map((row) =>
          String(row?.order_id) === String(updatedOrder.order_id)
            ? {
                ...row,
                reason: updatedOrder.reason,
                remark: updatedOrder.reason,
              }
            : row
        )
      );
    }
  };

  const handleApplyDateFilter = () => {
    fetchStatuses(true, false, selectedCompany);
    fetchProducts(statusFilter, selectedCompany, 1, pageSize);
  };

  const handleCompanyChange = (companyValue) => {
    setSelectedCompany(companyValue);
    setSelectedCategory('all');
    fetchStatuses(true, false, companyValue);
    fetchProducts(statusFilter, companyValue, 1, pageSize, null, null, null, 'all');
  };

  const handleCategoryChange = (categoryValue) => {
    setSelectedCategory(categoryValue);
    setCurrentPage(1);
    fetchProducts(statusFilter, selectedCompany, 1, pageSize, null, null, null, categoryValue);
  };

  // When a company is selected, only show that company's categories (API is company-scoped;
  // still filter by company id/name when response includes company metadata).
  const categoryOptions = (() => {
    if (!selectedCompany || selectedCompany === 'all') return categories;
    const selectedCompanyMeta = companies.find(
      (company) => String(company.id) === String(selectedCompany)
    );
    const selectedCompanyName = String(selectedCompanyMeta?.name || '')
      .trim()
      .toLowerCase();
    const getCategoryCompanyId = (category) =>
      category?.company?.id ?? category?.company_id ?? category?.company;
    const getCategoryCompanyName = (category) =>
      String(
        category?.company?.name ||
          category?.company_name ||
          (typeof category?.company === 'string' ? category.company : '') ||
          ''
      )
        .trim()
        .toLowerCase();
    const hasCompanyMeta = categories.some(
      (category) =>
        (getCategoryCompanyId(category) != null && getCategoryCompanyId(category) !== '') ||
        getCategoryCompanyName(category)
    );
    if (!hasCompanyMeta) return categories;
    return categories.filter((category) => {
      const categoryCompanyId = getCategoryCompanyId(category);
      if (categoryCompanyId != null && categoryCompanyId !== '') {
        return String(categoryCompanyId) === String(selectedCompany);
      }
      const categoryCompanyName = getCategoryCompanyName(category);
      return (
        Boolean(selectedCompanyName) &&
        Boolean(categoryCompanyName) &&
        categoryCompanyName === selectedCompanyName
      );
    });
  })();

  const handlePaymentTypeChange = (paymentTypeValue) => {
    setSelectedPaymentType(paymentTypeValue);
    setCurrentPage(1);
    fetchProducts(statusFilter, selectedCompany, 1, pageSize, null, null, paymentTypeValue);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchProducts(statusFilter, selectedCompany, value, pageSize);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchProducts(statusFilter, selectedCompany, 1, newSize);
  };

  useEffect(() => {
    setSelectedProducts([]);
    setIsAllSelected(false);
  }, [statusFilter]);

  useEffect(() => {
    setSelectedProducts([]);
    setIsAllSelected(false);
  }, [selectedCompany]);

  useEffect(() => {
    setSelectedProducts([]);
    setIsAllSelected(false);
  }, [selectedCategory]);

  useEffect(() => {
    setSelectedProducts([]);
    setIsAllSelected(false);
  }, [selectedPaymentType]);

  const handleProductSelect = (productId, isSelected) => {
    setSelectedProducts(prev => {
      if (isSelected) {
        const newSelected = [...prev, productId];
        setIsAllSelected(newSelected.length === products.length);
        return newSelected;
      } else {
        const newSelected = prev.filter(id => id !== productId);
        setIsAllSelected(false);
        return newSelected;
      }
    });
  };

  const handleSelectAll = async (selectAll) => {
    if (!selectAll) {
      setSelectedProducts([]);
      setIsAllSelected(false);
      return;
    }

    try {
      const userId = localStorage.getItem("user_id");
      const fromDate = formatDateForAPI(startDate);
      const toDate = formatDateForAPI(endDate);
      const statusParam = !statusFilter ? '' : `&status=${encodeURIComponent(statusFilter)}`;

      let url = `${API_ENDPOINTS.GET_products}?user_id=${userId}${statusParam}&type=normal`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      if (searchTerm && searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      const companyToUse = isCompanyUser && companyId ? companyId : selectedCompany;
      if (companyToUse && companyToUse !== "all") {
        url += `&company=${companyToUse}`;
      }
      if (selectedCategory && selectedCategory !== "all") {
        url += `&category=${selectedCategory}&product_category=${selectedCategory}`;
      }
      if (selectedPaymentType && selectedPaymentType !== "all") {
        url += `&payment_type=${encodeURIComponent(selectedPaymentType)}`;
      }

      const totalItems = Number(pagination?.total_items) || products.length || 0;
      const fetchSize = Math.max(totalItems, 1);
      url += `&page=1&page_size=${fetchSize}`;

      const response = await apiService.get(url);
      const allRows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.results)
          ? response.results
          : [];

      const allProductIds = allRows
        .map((product) => product?.id ?? product?.order_id)
        .filter((id) => id != null && id !== "");

      setSelectedProducts(allProductIds);
      setIsAllSelected(true);
    } catch (error) {
      console.error("Error selecting all products across pages:", error);
      const currentPageIds = products
        .map((product) => product?.id ?? product?.order_id)
        .filter((id) => id != null && id !== "");
      setSelectedProducts(currentPageIds);
      setIsAllSelected(true);
    }
  };

  const handleExport = (format) => {
    const selectedData = products.filter(product => 
      selectedProducts.includes(product.id)
    );

    if (selectedData.length === 0) {
      alert('Please select products to export');
      return;
    }

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `products_export_${timestamp}`;

    if (format === 'excel') {
      exportToExcel(selectedData, `${filename}.xlsx`);
    } else if (format === 'csv') {
      exportToCSV(selectedData, `${filename}.csv`);
    }
  };

  const handleDownloadZip = async (mode) => {
    try {
      if (selectedProducts.length === 0) {
        alert('Please select orders first');
        return;
      }

      // Resolve selected rows across all pages for current tab/filters.
      const userId = localStorage.getItem("user_id");
      const fromDate = formatDateForAPI(startDate);
      const toDate = formatDateForAPI(endDate);
      const statusParam = !statusFilter ? '' : `&status=${encodeURIComponent(statusFilter)}`;

      let url = `${API_ENDPOINTS.GET_products}?user_id=${userId}${statusParam}&type=normal`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      if (searchTerm && searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }
      const companyToUse = isCompanyUser && companyId ? companyId : selectedCompany;
      if (companyToUse && companyToUse !== "all") {
        url += `&company=${companyToUse}`;
      }
      if (selectedCategory && selectedCategory !== "all") {
        url += `&category=${selectedCategory}&product_category=${selectedCategory}`;
      }
      if (selectedPaymentType && selectedPaymentType !== "all") {
        url += `&payment_type=${encodeURIComponent(selectedPaymentType)}`;
      }
      const totalItems = Number(pagination?.total_items) || products.length || 0;
      const fetchSize = Math.max(totalItems, 1);
      url += `&page=1&page_size=${fetchSize}`;

      const response = await apiService.get(url);
      const allRows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.results)
          ? response.results
          : [];

      const selectedKeySet = new Set(selectedProducts.map((id) => String(id)));
      const selectedData = allRows.filter((product) => {
        const idKey = product?.id != null ? String(product.id) : null;
        const orderKey = product?.order_id != null ? String(product.order_id) : null;
        return (idKey && selectedKeySet.has(idKey)) || (orderKey && selectedKeySet.has(orderKey));
      });

      const orderIds = selectedData
        .map(product => product.order_id)
        .filter(Boolean);

      if (orderIds.length === 0) {
        alert('Selected orders do not have valid order IDs');
        return;
      }

      const query = new URLSearchParams({
        order_ids: orderIds.join(','),
        type: mode
      }).toString();

      const filename = `orders_${mode}_${new Date().toISOString().slice(0, 10)}.zip`;

      await apiService.downloadFile(`${API_ENDPOINTS.GET_generate_zip_file}?${query}`, filename);
    } catch (error) {
      console.error('Error creating ZIP:', error);
      alert(error?.message || 'Failed to download documents ZIP. Please try again.');
    }
  };

  return (
    <Box
      pt={2}
      pb={4}
      px={{ xs: 1, sm: 2, md: 3 }}
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
        animation: isBlinking ? 'blink 420ms ease-in-out' : 'none',
        '@keyframes blink': {
          '0%': { opacity: 1 },
          '50%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
      }}
    >
      {/* Advanced Filters */}
      <FilterContainer>
        <FilterHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <FilterList color="primary" />
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Filters & Search
              </Typography>
              {(() => {
                const startDateStr = toDateInputValue(startDate);
                const endDateStr = toDateInputValue(endDate);
                const hasDateRange = startDateStr && endDateStr;
                const hasActiveFilters = searchTerm || hasDateRange;
                
                return hasActiveFilters && (
                  <>
                    <StatusChip
                      statuscolor={theme.palette.success.main}
                      sx={{ ml: 1 }}
                    >
                      Active
                    </StatusChip>
                    {searchTerm && (
                      <StatusChip
                        statuscolor={theme.palette.info.main}
                        sx={{ ml: 0.5 }}
                      >
                        🔍 {searchTerm}
                      </StatusChip>
                    )}
                    {hasDateRange && (
                      <StatusChip
                        statuscolor={theme.palette.info.main}
                        sx={{ ml: 0.5 }}
                      >
                        📅 {formatDateChipLabel(startDateStr)} - {formatDateChipLabel(endDateStr)}
                      </StatusChip>
                    )}
                  </>
                );
              })()}
            </Box>
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            >
              <ExpandMore />
            </IconButton>
          </Box>
        </FilterHeader>

        <Collapse in={showFilters}>
          <FilterContent>
            <Typography variant="caption" sx={{ mb: 1, display: "block", fontWeight: 600, color: "text.secondary" }}>
              Date Range Filter
            </Typography>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={3.5}>
                <DatePickerField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={toDateInputValue(startDate)}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setStartDate(newDate);
                    setIsResetClicked(false);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: toDateInputValue(endDate) || toDateInputValue(new Date()),
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3.5}>
                <DatePickerField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={toDateInputValue(endDate)}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    setEndDate(newDate);
                    setIsResetClicked(false);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: toDateInputValue(startDate) || toDateInputValue(new Date(new Date().setDate(1))),
                    max: toDateInputValue(new Date()),
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <ActionButton
                  fullWidth
                  variant="contained"
                  startIcon={<FilterList />}
                  onClick={handleApplyDateFilter}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    },
                  }}
                >
                  Apply
                </ActionButton>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <ActionButton
                  fullWidth
                  variant="outlined"
                  startIcon={
                    <Refresh
                      sx={{
                        animation: isLoading ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                  }
                  onClick={() => {
                    setIsBlinking(true);
                    const defaultStartDate = toDateInputValue(new Date(new Date().setDate(1)));
                    const defaultEndDate = toDateInputValue(new Date());
                    const companyToUse = isCompanyUser ? companyId : 'all';
                    setSearchValue('');
                    setSearchTerm('');
                    setHasSearched(false);
                    setStartDate(defaultStartDate);
                    setEndDate(defaultEndDate);
                    setSelectedPaymentType('all');
                    setIsResetClicked(true);
                    if (!isCompanyUser) setSelectedCompany('all');
                    setCurrentPage(1);
                    fetchProducts(
                      statusFilter,
                      companyToUse,
                      1,
                      pageSize,
                      "",
                      { startDate: defaultStartDate, endDate: defaultEndDate },
                      'all'
                    );
                    setTimeout(() => setIsBlinking(false), 420);
                  }}
                  sx={{
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    '&:hover': {
                      borderColor: theme.palette.error.dark,
                      backgroundColor: alpha(theme.palette.error.main, 0.05)
                    },
                  }}
                >
                  Refresh
                </ActionButton>
              </Grid>
            </Grid>
          </FilterContent>
        </Collapse>
      </FilterContainer>

      {/* Status Tabs */}
      <TabContainer>
        {statuses.length === 0 ? (
          <Box sx={{ display: "flex", gap: 1, overflow: "hidden", py: 1.25, px: 0.5 }}>
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton
                key={`app-status-tab-skeleton-${index}`}
                animation="wave"
                variant="rounded"
                width={118}
                height={38}
                sx={{ flexShrink: 0, borderRadius: 2 }}
              />
            ))}
          </Box>
        ) : (
        <StyledTabs
          value={statusFilter}
          onChange={handleStatusChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {statuses.map((s) => {
            const isActiveTab = statusFilter === s.name;
            const isDisabled = isOrderStatusTabDisabled("appOrders", s);
            return (
              <StyledTab
                key={s.id || s.name}
                value={s.name}
                disabled={isDisabled}
                sx={isDisabled ? {
                  opacity: 0.4,
                  color: "text.disabled",
                  cursor: "not-allowed",
                  textDecoration: "line-through",
                  "&.Mui-disabled": { opacity: 0.4 },
                } : undefined}
                label={
                  <Tooltip
                    title={isDisabled ? "You don't have access to this status tab" : ""}
                    arrow
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {isDisabled ? <LockOutlinedIcon sx={{ fontSize: 14 }} /> : <span>{getStatusIcon(s.category || s.name)}</span>}
                      <span>{s.name}</span>
                      <OrderStatusCountBadge
                        data-order-status-count
                        active={isActiveTab && !isDisabled}
                      >
                        {s.count || 0}
                      </OrderStatusCountBadge>
                    </Box>
                  </Tooltip>
                }
              />
            );
          })}
        </StyledTabs>
        )}
      </TabContainer>

      {/* Search Controls & Action Buttons */}
      <Card
        sx={{
          p: { xs: 1.25, sm: 2 },
          mb: 1.5,
          overflow: "visible",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: { xs: "wrap", md: "nowrap" },
            gap: 1.25,
            alignItems: "center",
            width: "100%",
            maxWidth: "100%",
            overflow: "visible",
            py: 0.5,
            // 320px–868px: wrap controls so they don't overlap or overflow
            [theme.breakpoints.down(869)]: {
              flexWrap: "wrap",
            },
          }}
        >
          <SearchInput
            bordered={true}
            placeholder="Search by Order ID,Customer,Mobile N0..."
            onChange={(e) => {
              setSearchValue(e.target.value);
              if (hasSearched && e.target.value !== searchTerm) {
                setHasSearched(false);
              }
            }}
            value={searchValue}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
            sx={{
              minWidth: 0,
              height: 40,
              flex: "1 1 160px",
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: "8px",
              px: 1.5,
              py: 0,
              backgroundColor: "background.paper",
              [theme.breakpoints.down(869)]: {
                flex: "1 1 calc(100% - 108px)",
                minWidth: 0,
                maxWidth: "100%",
              },
              [theme.breakpoints.down(400)]: {
                flex: "1 1 100%",
              },
            }}
            size={isMobile ? "small" : "medium"}
          />
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleSearch}
            disabled={
              !searchValue.trim() ||
              (searchValue === searchTerm && hasSearched)
            }
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              flexShrink: 0,
              height: 40,
              minWidth: 90,
              px: 1.5,
              boxShadow: "none",
              bgcolor: searchValue.trim() ? "primary.main" : "#E9EAEF",
              color: searchValue.trim() ? "common.white" : "#8B98AD",
              "&:hover": {
                bgcolor: searchValue.trim() ? "primary.dark" : "#DEE1E8",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                bgcolor: "#E9EAEF",
                color: "#8B98AD",
                opacity: 1,
              },
              [theme.breakpoints.down(869)]: {
                flex: "0 0 auto",
              },
              [theme.breakpoints.down(400)]: {
                flex: "1 1 100%",
                minWidth: 0,
              },
            }}
          >
            Search
          </Button>

          <FormControl
            size="small"
            sx={{
              flex: "1 1 130px",
              minWidth: 0,
              [theme.breakpoints.down(869)]: {
                flex: "1 1 calc(50% - 6px)",
                minWidth: "calc(50% - 6px)",
              },
              [theme.breakpoints.down(480)]: {
                flex: "1 1 100%",
                minWidth: "100%",
              },
            }}
          >
            <InputLabel>Payment Type</InputLabel>
            <Select
              value={selectedPaymentType}
              label="Payment Type"
              onChange={(e) => handlePaymentTypeChange(e.target.value)}
              renderValue={(value) => {
                if (value === "all") return "All Payment Types";
                return PAYMENT_TYPE_OPTIONS.find((o) => o.value === value)?.label || value;
              }}
            >
              <MenuItem value="all">All Payment Types</MenuItem>
              {PAYMENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              flex: "1 1 150px",
              minWidth: 0,
              [theme.breakpoints.down(869)]: {
                flex: "1 1 calc(50% - 6px)",
                minWidth: "calc(50% - 6px)",
              },
              [theme.breakpoints.down(480)]: {
                flex: "1 1 100%",
                minWidth: "100%",
              },
            }}
          >
            <InputLabel>Company</InputLabel>
            <Select
              value={selectedCompany}
              label="Company"
              onChange={(e) => handleCompanyChange(e.target.value)}
              disabled={isCompanyUser}
              renderValue={(value) => {
                const name =
                  value === "all"
                    ? "All Companies"
                    : companies.find((c) => String(c.id) === String(value))?.name || String(value);
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, width: "100%" }}>
                    <Chip
                      label={name}
                      size="small"
                      color={value === "all" ? "default" : "secondary"}
                      variant={value === "all" ? "filled" : "outlined"}
                      sx={{ maxWidth: "100%" }}
                    />
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      }}
                    >
                      {name}
                    </Box>
                  </Box>
                );
              }}
            >
              {!isCompanyUser && (
                <MenuItem value="all">
                  <Chip label="All Companies" size="small" color="default" sx={{ mr: 1 }} />
                  All Companies
                </MenuItem>
              )}
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  <Chip
                    label={company.name}
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                  {company.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{
              flex: "1 1 150px",
              minWidth: 0,
              [theme.breakpoints.down(869)]: {
                flex: "1 1 calc(50% - 6px)",
                minWidth: "calc(50% - 6px)",
              },
              [theme.breakpoints.down(480)]: {
                flex: "1 1 100%",
                minWidth: "100%",
              },
            }}
          >
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Category"
              onChange={(e) => handleCategoryChange(e.target.value)}
              renderValue={(value) => {
                if (value === "all") {
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, width: "100%" }}>
                      <Chip label="All Categories" size="small" color="default" sx={{ maxWidth: "100%" }} />
                      <Box
                        component="span"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        All Categories
                      </Box>
                    </Box>
                  );
                }
                const category = categoryOptions.find((c) => String(c.id) === String(value))
                  || categories.find((c) => String(c.id) === String(value));
                const chipLabel = category?.company?.name || category?.name || String(value);
                const name = category?.name || String(value);
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, width: "100%" }}>
                    <Chip label={chipLabel} size="small" color="primary" variant="outlined" sx={{ maxWidth: "100%" }} />
                    <Box
                      component="span"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        minWidth: 0,
                      }}
                    >
                      {name}
                    </Box>
                  </Box>
                );
              }}
            >
              <MenuItem value="all">
                <Chip label="All Categories" size="small" color="default" sx={{ mr: 1 }} />
                All Categories
              </MenuItem>
              {categoryOptions.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  <Chip
                    label={category.company?.name || category.name}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<GetApp />}
            onClick={() => setAdvancedExportModal(true)}
            disabled={!shouldEnableButton("advanced_export")}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              flexShrink: 0,
              height: 40,
              whiteSpace: "nowrap",
              opacity: !shouldEnableButton("advanced_export") ? 0.6 : 1,
              [theme.breakpoints.down(869)]: {
                flex: "1 1 calc(50% - 6px)",
                minWidth: 0,
                maxWidth: "100%",
                px: 1,
              },
              [theme.breakpoints.down(480)]: {
                flex: "1 1 100%",
              },
            }}
          >
            Advanced Export
          </Button>

          <FormControl
            size="small"
            sx={{
              width: 100,
              minWidth: 100,
              flex: "0 0 100px",
              [theme.breakpoints.down(869)]: {
                flex: "1 1 100px",
                width: "auto",
                minWidth: 96,
              },
            }}
          >
            <InputLabel>Rows</InputLabel>
            <Select
              value={pageSize}
              label="Rows"
              onChange={handlePageSizeChange}
            >
              {pageSizeOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {isLoading && !summaryTotals ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                px: 1.5,
                py: 0.5,
                minWidth: 110,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.success.main, 0.08),
                border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
                flex: "0 0 auto",
              }}
            >
              <Skeleton animation="wave" variant="text" width={72} height={14} />
              <Skeleton animation="wave" variant="text" width={88} height={20} />
            </Box>
          ) : summaryTotals && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                px: 1.5,
                py: 0.5,
                minWidth: 110,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.success.main, 0.08),
                border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
                flex: "0 0 auto",
                [theme.breakpoints.down(869)]: {
                  flex: "1 1 auto",
                  minWidth: 0,
                },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ lineHeight: 1.2, fontSize: 11 }}
              >
                Total Amount
              </Typography>
              <Typography
                variant="body2"
                fontWeight={700}
                color="success.dark"
                sx={{ lineHeight: 1.3 }}
              >
                {formatSummaryAmount(summaryTotals.total_amount)}
              </Typography>
            </Box>
          )}
        </Box>
      </Card>

      {/* Orders Table */}
      <Card sx={{ marginTop: 2, overflowX: 'auto', maxWidth: '100%', width: '100%' }}>
        <ProductList
          data={products} 
          searchQuery={searchTerm} 
          statusFilter={statusFilter}
          isLoading={isLoading} 
          onRefresh={handleRefreshWithBlink}
          onOrderEditSuccess={handleOrderEditSuccess}
          selectedProducts={selectedProducts}
          onProductSelect={handleProductSelect}
          onSelectAll={handleSelectAll}
          isAllSelected={isAllSelected}
          onExport={handleExport}
          onZipDownload={handleDownloadZip}
          currentPage={pagination?.current_page}
          pageSize={pagination?.page_size}
          shouldEnableButton={shouldEnableButton}
          listFilters={{
            startDate,
            endDate,
            selectedCompany,
            selectedCategory,
            showFilters,
          }}
        />
      </Card>

      {/* Showing range and pagination below the table */}
      {pagination && !isLoading && (
        <Box sx={{ mt: 2 }}>
          <AppPaginationBar
            pagination={pagination}
            pageSize={pagination?.page_size}
            onPageChange={handlePageChange}
            itemLabel="orders"
          />
        </Box>
      )}

      {/* Advanced Export Modal */}
      <AdvancedExportModal
        open={advancedExportModal}
        onClose={() => setAdvancedExportModal(false)}
        onRefresh={handleRefreshWithBlink}
      />
    </Box>
  );
};

export default Products;